import * as React from "react";

/**
 * Width engine for the canonical docked drawer (`DialogContent sheetSide="right"`).
 *
 * Extracted verbatim from the marketplace peek (the proven implementation) so the 50%/100%
 * presets + drag-resize become part of the shared component rather than hand-assembled per
 * surface — see ADR 2026-07-12 and $sg-modal-to-drawer. A drawer opens at 50% of the viewport,
 * the operator can drag the left edge (double-click to snap back to 50%), and the chosen width is
 * remembered per-browser under `storageKey`.
 *
 * SSR/jsdom-safe: every `window` access is guarded, so it renders at a sane default width with no
 * DOM.
 */

export const DRAWER_MIN_WIDTH_PX = 420;
/** Width at/above this fraction of the viewport reads as the 100% preset. */
const FULL_FRACTION = 0.97;
/** Width at/below this fraction reads as the 50% preset (anything between is a custom drag). */
const HALF_FRACTION = 0.6;

export interface DrawerWidth {
  /** Current pixel width to apply to the panel. */
  width: number;
  /** Whether the current width reads as the 50% preset. */
  isHalf: boolean;
  /** Whether the current width reads as the 100% preset. */
  isFull: boolean;
  /**
   * Whether the drawer is *engaged* at 100% — an in-memory latch (NOT persisted) the drawer uses to
   * decide whether to hold a menu-condense request (ENT-3477). Set by the 100% preset and by
   * hydrating/reseeding a width that reads as full; cleared by the 50% preset and double-click
   * reset; deliberately UNCHANGED by dragging. Distinct from `isFull`, which is a pure width
   * predicate: dragging to full width reads `isFull` but must never engage.
   */
  fullEngaged: boolean;
  /** Pointer-down handler for the left-edge drag handle. */
  startResize: (event: React.PointerEvent) => void;
  /** Snap back to 50% (double-click the handle). */
  resetWidth: () => void;
  /** Set an explicit preset (0.5 = 50%, 1 = 100% coverage). */
  setPreset: (fraction: number) => void;
  /** Re-seed the 50% default against the LIVE viewport on open, unless the operator has explicitly
   *  sized the drawer. Call this when the drawer actually opens — the wrapper can stay mounted with
   *  a stale viewport otherwise. */
  reseedDefault: () => void;
}

function clamp(value: number): number {
  if (typeof window === "undefined" || !window.innerWidth) {
    return Math.max(value, DRAWER_MIN_WIDTH_PX);
  }
  // Min keeps the record readable; max is full-viewport coverage (the 100% preset).
  return Math.min(Math.max(value, DRAWER_MIN_WIDTH_PX), window.innerWidth);
}

function readStored(storageKey: string): number | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored >= DRAWER_MIN_WIDTH_PX ? stored : null;
  } catch {
    return null;
  }
}

function persist(storageKey: string, value: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    /* localStorage unavailable — width simply won't persist */
  }
}

/**
 * Provenance flag: did the persisted width come from the 100% PRESET (vs. a drag / 50% / custom)?
 * Engagement (condensing the nav) must key on this, not on the width reading as full — otherwise a
 * drag to the viewport edge would silently re-engage on reopen, breaking the "drag never engages"
 * contract (ENT-3477, Codex review). Stored under a companion key so the bare width key stays a
 * plain number and legacy values (no flag) hydrate as non-engaged.
 */
const fullPresetKey = (storageKey: string) => `${storageKey}:fullpreset`;

function readFullPreset(storageKey: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(fullPresetKey(storageKey)) === "1";
  } catch {
    return false;
  }
}

function persistFullPreset(storageKey: string, on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (on) window.localStorage.setItem(fullPresetKey(storageKey), "1");
    else window.localStorage.removeItem(fullPresetKey(storageKey));
  } catch {
    /* localStorage unavailable — provenance simply won't persist */
  }
}

/**
 * @param storageKey per-surface localStorage key, so each drawer remembers its own width.
 * @param fullInsetPx horizontal space to leave for a still-visible left rail at 100% (ENT-3477).
 *   Default 0 keeps the historical behavior (100% = full viewport). When a canonical drawer is
 *   inside a `SidebarProvider`, the drawer passes the collapsed icon-rail width so its 100% width
 *   lands flush against the rail rather than covering it. It also shifts the full-detection
 *   threshold, so the 100% button still highlights while engaged.
 *
 * The 50% default is computed on mount against the live viewport, AND re-seeded on each open via
 * `reseedDefault()` — because the `DialogContent` wrapper can stay mounted while the dialog is
 * closed (Marketplace renders it unconditionally), so a resize between mount and open would
 * otherwise leave a stale width (Codex P2). A persisted custom width (from a drag or a preset) is
 * read back on mount and wins over the 50% default, and is preserved across opens.
 */
export function useDrawerWidth(storageKey: string, fullInsetPx = 0): DrawerWidth {
  const [width, setWidth] = React.useState<number>(() => {
    if (typeof window === "undefined" || !window.innerWidth) return 640;
    const stored = readStored(storageKey);
    if (stored !== null) return Math.min(stored, window.innerWidth);
    // Clamp the 50% default to the min width (collapsing to full-viewport when even the
    // minimum can't fit) so a narrow phone doesn't open a ~195px, unusable drawer.
    return clamp(Math.round(window.innerWidth * 0.5));
  });

  // Whether the operator (or a persisted preference) has set an explicit width.
  const manuallySized = React.useRef<boolean>(readStored(storageKey) !== null);

  // In-memory 100%-engagement latch (ENT-3477); never persisted. Starts false and is decided at
  // open-time by `reseedDefault()` (which reads the hydrated width), so a persisted full width only
  // engages once the drawer is actually open — not while the wrapper sits mounted-but-closed.
  const [fullEngaged, setFullEngaged] = React.useState(false);

  const readsFull = React.useCallback(
    (value: number): boolean => {
      if (typeof window === "undefined" || !window.innerWidth) return false;
      return value >= (window.innerWidth - fullInsetPx) * FULL_FRACTION;
    },
    [fullInsetPx],
  );

  const startResize = React.useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      let latest = width;
      const onMove = (moveEvent: PointerEvent) => {
        latest = clamp(window.innerWidth - moveEvent.clientX);
        setWidth(latest);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.userSelect = "";
        manuallySized.current = true;
        persist(storageKey, latest);
        // A drag is never the 100% preset — clear the provenance so a drag to the edge does not
        // re-engage condensation on the next open. Live `fullEngaged` is left untouched (R3: a drag
        // while engaged keeps the menu condensed until a restore trigger).
        persistFullPreset(storageKey, false);
      };
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [width, storageKey],
  );

  const resetWidth = React.useCallback(() => {
    if (typeof window === "undefined" || !window.innerWidth) return;
    const next = clamp(Math.round(window.innerWidth * 0.5));
    setWidth(next);
    manuallySized.current = true;
    // Double-click reset is the 50% preset — engagement ends (state machine, R2.3).
    setFullEngaged(false);
    persist(storageKey, next);
    persistFullPreset(storageKey, false);
  }, [storageKey]);

  const setPreset = React.useCallback(
    (fraction: number) => {
      if (typeof window === "undefined" || !window.innerWidth) return;
      // The 100% preset leaves room for a still-visible rail; every other preset uses the plain
      // fraction of the viewport (50% math unchanged).
      const isFullPreset = fraction >= 1;
      const target = isFullPreset ? window.innerWidth - fullInsetPx : window.innerWidth * fraction;
      const next = clamp(Math.round(target));
      setWidth(next);
      manuallySized.current = true;
      // 100% engages; any other preset (50%) disengages (state machine). Persist the provenance so
      // a reopen re-engages ONLY a real 100%-preset width, never a drag that happened to reach full.
      setFullEngaged(isFullPreset);
      persist(storageKey, next);
      persistFullPreset(storageKey, isFullPreset);
    },
    [storageKey, fullInsetPx],
  );

  // Re-seed the 50% default against the LIVE viewport on open. Mirrors the deleted marketplace
  // effect: recompute unless the operator has explicitly sized the drawer (drag or preset), so a
  // resize while the wrapper sat mounted-but-closed doesn't strand a stale width. Also (re)decides
  // engagement from the width the drawer opens with, so a persisted full width engages on open
  // (ENT-3477) — read from storage rather than the possibly-stale `width` closure so this callback
  // stays stable and doesn't re-fire the open effect on every width change.
  const reseedDefault = React.useCallback(() => {
    if (typeof window === "undefined" || !window.innerWidth) return;
    const vw = window.innerWidth;
    // Engagement is decided by PROVENANCE (was the persisted width the 100% preset?), never by the
    // width happening to read as full — so a drag to the edge never re-engages on reopen (Codex P2).
    const engaged = manuallySized.current && readFullPreset(storageKey);
    if (engaged) {
      // Normalize the persisted 100% preset to the current inset-aware target: a legacy
      // full-viewport value (saved before the inset existed) or one hydrated wider than the viewport
      // would otherwise cover the supposedly-visible rail. Persist the normalized value + provenance.
      const target = clamp(vw - fullInsetPx);
      setWidth(target);
      manuallySized.current = true;
      persist(storageKey, target);
      persistFullPreset(storageKey, true);
    } else if (!manuallySized.current) {
      // No explicit sizing yet — (re)seed the 50% default against the live viewport.
      setWidth(clamp(Math.round(vw * 0.5)));
    }
    // else: a persisted custom/drag/50% width — keep it as hydrated, just mark it not engaged.
    setFullEngaged(engaged);
  }, [storageKey, fullInsetPx]);

  const vw = typeof window !== "undefined" ? window.innerWidth : 0;
  const isFull = vw > 0 && readsFull(width);
  const isHalf = vw > 0 && !isFull && width <= vw * HALF_FRACTION;

  return { width, isHalf, isFull, fullEngaged, startResize, resetWidth, setPreset, reseedDefault };
}
